import { useEffect, useState } from "react";
import MainLayout from "../../components/layouts/MainLayout";
import { formationApi } from "../../api/formationApi";
import Swal from "sweetalert2";
import { message } from "antd";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Auth } from "../../utils/Auth";
import { PlusOutlined, MinusCircleOutlined, LeftCircleOutlined, DeleteOutlined } from "@ant-design/icons";

const EditFormation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        content: '',
        highlighting: false,
        is_training: true,
        is_premium: false,
    });

    const [loading, setLoading] = useState(false);
    const [quiz, setQuiz] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [mainImage, setMainImage] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState(null);
    const [existingMainImage, setExistingMainImage] = useState(null);
    const abortController = new AbortController();

    // Charger les données de la formation
    const fetchformation = async () => {
        setLoading(true);
        try {
            const res = await formationApi.getFormationDetails(`${id}`, abortController.signal);
            const formation = res.data;

            setFormData({
                title: formation.title || "",
                description: formation.description || "",
                content: formation.content || "",
                city: formation.city || "",  
                is_training: formation.is_training === 1,
                is_premium: formation.is_premium === 1,
            });

            // Charger l'image principale existante
            if (formation.main_image) {
                setExistingMainImage(formation.main_image);
            }

            // Charger les images existantes
            if (formation.medias && formation.medias.length > 0) {
                setExistingImages(formation.medias.filter(img => img.etat === 1));
            }

            // Charger le quiz si présent
            if (formation.is_training === 1 && formation.quiz) {
                setQuiz(
                    formation?.quiz?.questions?.map((q) => ({
                        question: q.libelle || "",
                        answers: (q.answers || [])?.map((a) => ({
                            libelle: a.libelle || "",
                            is_true: a.is_true === 1,
                        })),
                    })) || []
                );
            }
        } catch (error) {
            message.error("Impossible de charger les données de la formation");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchformation();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    // Gestion de l'image principale
    const handleMainImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setMainImage(file);
        
        const reader = new FileReader();
        reader.onloadend = () => {
            setMainImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const removeMainImage = () => {
        setMainImage(null);
        setMainImagePreview(null);
    };

    // Gestion des images multiples
    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        
        if (files.length === 0) return;

        const newFiles = [...selectedImages, ...files];
        setSelectedImages(newFiles);

        const newPreviews = [];
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                newPreviews.push({
                    file: file,
                    preview: reader.result,
                });
                if (newPreviews.length === files.length) {
                    setImagePreviews([...imagePreviews, ...newPreviews]);
                }
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (index) => {
        const newImages = selectedImages.filter((_, i) => i !== index);
        const newPreviews = imagePreviews.filter((_, i) => i !== index);
        setSelectedImages(newImages);
        setImagePreviews(newPreviews);
    };

    // Gestion du quiz
    const addQuestion = () => {
        setQuiz([
            ...quiz,
            {
                question: "",
                answers: [
                    { libelle: "", is_true: false },
                    { libelle: "", is_true: false },
                ],
            },
        ]);
    };

    const removeQuestion = (questionIndex) => {
        setQuiz(quiz.filter((_, index) => index !== questionIndex));
    };

    const updateQuestion = (questionIndex, value) => {
        const updatedQuiz = [...quiz];
        updatedQuiz[questionIndex].question = value;
        setQuiz(updatedQuiz);
    };

    const addAnswer = (questionIndex) => {
        const updatedQuiz = [...quiz];
        updatedQuiz[questionIndex].answers.push({ libelle: "", is_true: false });
        setQuiz(updatedQuiz);
    };

    const removeAnswer = (questionIndex, answerIndex) => {
        const updatedQuiz = [...quiz];
        updatedQuiz[questionIndex].answers = updatedQuiz[questionIndex].answers.filter(
            (_, index) => index !== answerIndex
        );
        setQuiz(updatedQuiz);
    };

    const updateAnswer = (questionIndex, answerIndex, field, value) => {
        const updatedQuiz = [...quiz];
        if (field === "is_true") {
            updatedQuiz[questionIndex].answers.forEach((answer, index) => {
                answer.is_true = index === answerIndex;
            });
        } else {
            updatedQuiz[questionIndex].answers[answerIndex][field] = value;
        }
        setQuiz(updatedQuiz);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formDataToSend = new FormData();

            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('content', formData.content || '');
            formDataToSend.append('city', formData.city || '');            
            formDataToSend.append('is_training', 1);
            formDataToSend.append('is_premium', formData.is_premium ? 1 : 0);

            // Ajouter la nouvelle image principale si sélectionnée
            if (mainImage) {
                formDataToSend.append('main_image', mainImage);
            }

            // Ajouter les nouvelles images avec le nom exact medias[]
            selectedImages.forEach((file) => {
                formDataToSend.append('medias[]', file);
            });

            // Ajouter le quiz si is_training est coché
            if (formData.is_training && quiz.length > 0) {
                formDataToSend.append('quiz', JSON.stringify(
                    quiz.map((q) => ({
                        question: q.question,
                        answers: q.answers.map((a) => ({
                            libelle: a.libelle,
                            is_true: a.is_true ? 1 : 0,
                        })),
                    }))
                ));
            }

            const res = await formationApi.updateFormation(
                id,
                formDataToSend,
                abortController.signal
            );

            console.log(res);

            if (!res.success) {
                return Swal.fire({
                    title: "Impossible de mettre à jour la formation",
                    text: res.message,
                });
            }

            message.success("Formation modifiée avec succès");
            navigate(`/formation/${id}/details-quiz`);
        } catch (error) {
            message.error("Une erreur est survenue lors de la modification de la formation");
            const messages = await error.messages ?? error.message;

            if (messages instanceof Array) {
                return Swal.fire({
                    title:
                        messages.join("\n") !== "Unauthenticated"
                            ? "Erreur détectée"
                            : "Session expirée",
                    text:
                        messages.join("\n") !== "Unauthenticated"
                            ? "Veuillez vous reconnecter."
                            : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then((result) => {
                    Auth.removeSessionToken();
                    return navigate(`/login`);
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <MainLayout>
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">Modifier la formation</h2>
                    <Link
                        to={`/formations`}
                        className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                        <LeftCircleOutlined className="mr-2" />
                        Retour
                    </Link>
                </div>

                {loading && !formData.title ? (
                    <div className="text-center py-8">
                        <p>Chargement des données...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {/* Titre */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Titre <span className="text-red-600 text-sm italic">(Obligatoire)</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Description */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Contenu riche */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contenu de la formation
                            </label>
                            <textarea
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                rows="10"
                                placeholder="Saisissez le contenu de la formation (HTML autorisé)"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Vous pouvez utiliser du HTML pour formater le contenu
                            </p>
                        </div>

                        {/* Image principale existante */}
                        {existingMainImage && !mainImagePreview && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Image principale actuelle
                                </label>
                                <div className="relative inline-block">
                                    <img
                                        src={existingMainImage}
                                        alt="Image principale actuelle"
                                        className="w-64 h-48 object-cover rounded-md border border-gray-300"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Upload nouvelle image principale */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {existingMainImage ? "Remplacer l'image principale" : "Image d'illustration principale"}
                            </label>
                            <input
                                type="file"
                                name="main_image"
                                accept="image/*"
                                onChange={handleMainImageChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            />
                            {mainImagePreview && (
                                <div className="mt-4 relative inline-block">
                                    <img
                                        src={mainImagePreview}
                                        alt="Aperçu nouvelle image principale"
                                        className="w-64 h-48 object-cover rounded-md border border-gray-300"
                                    />
                                    <button
                                        type="button"
                                        onClick={removeMainImage}
                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                        <DeleteOutlined />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Images existantes */}
                        {existingImages.length > 0 && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Images existantes
                                </label>
                                <div className="grid grid-cols-4 gap-4">
                                    {existingImages.map((image, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                src={image.file_path}
                                                alt={`Image ${index + 1}`}
                                                className="w-full h-32 object-cover rounded-md border border-gray-300"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Upload de nouvelles images */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ajouter de nouvelles images
                            </label>
                            <input
                                type="file"
                                name="medias[]"
                                multiple
                                accept="image/*"
                                onChange={handleImageChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            />
                            {imagePreviews.length > 0 && (
                                <div className="mt-4 grid grid-cols-4 gap-4">
                                    {imagePreviews.map((preview, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                src={preview.preview}
                                                alt={`Preview ${index + 1}`}
                                                className="w-full h-32 object-cover rounded-md border border-gray-300"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                            >
                                                <DeleteOutlined />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Mise en avant */}
                        <div className="mb-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="is_premium"
                                    checked={formData.is_premium}
                                    onChange={handleChange}
                                    className="mr-2"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Formation premium
                                </span>
                            </label>
                        </div>

                        {/* Is Training */}
                        <div className="mb-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="is_training"
                                    checked={formData.is_training}
                                    onChange={handleChange}
                                    className="mr-2"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Formation avec quiz
                                </span>
                            </label>
                        </div>

                        {/* Formulaire Quiz */}
                        {formData.is_training && (
                            <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold">Quiz</h3>
                                    <button
                                        type="button"
                                        onClick={addQuestion}
                                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
                                    >
                                        <PlusOutlined className="mr-1" />
                                        Ajouter une question
                                    </button>
                                </div>

                                {quiz.map((question, questionIndex) => (
                                    <div
                                        key={questionIndex}
                                        className="mb-4 p-4 border border-gray-200 rounded-md bg-white"
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Question {questionIndex + 1}
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => removeQuestion(questionIndex)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <MinusCircleOutlined />
                                            </button>
                                        </div>
                                        <input
                                            type="text"
                                            value={question.question}
                                            onChange={(e) =>
                                                updateQuestion(questionIndex, e.target.value)
                                            }
                                            placeholder="Libellé de la question"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md mb-3"
                                        />

                                        <div className="ml-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Réponses
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswer(questionIndex)}
                                                    className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                                >
                                                    <PlusOutlined className="mr-1" />
                                                    Ajouter une réponse
                                                </button>
                                            </div>

                                            {question.answers.map((answer, answerIndex) => (
                                                <div
                                                    key={answerIndex}
                                                    className="mb-2 flex items-center gap-2"
                                                >
                                                    <input
                                                        type="text"
                                                        value={answer.libelle}
                                                        onChange={(e) =>
                                                            updateAnswer(
                                                                questionIndex,
                                                                answerIndex,
                                                                "libelle",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="Libellé de la réponse"
                                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md"
                                                    />
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            name={`correct-${questionIndex}`}
                                                            checked={answer.is_true}
                                                            onChange={() =>
                                                                updateAnswer(
                                                                    questionIndex,
                                                                    answerIndex,
                                                                    "is_true",
                                                                    true
                                                                )
                                                            }
                                                            className="mr-1"
                                                        />
                                                        <span className="text-sm text-gray-700">
                                                            Bonne réponse
                                                        </span>
                                                    </label>
                                                    {question.answers.length > 2 && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                removeAnswer(questionIndex, answerIndex)
                                                            }
                                                            className="text-red-600 hover:text-red-800"
                                                        >
                                                            <MinusCircleOutlined />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {quiz.length === 0 && (
                                    <p className="text-gray-500 text-sm italic">
                                        Aucune question ajoutée. Cliquez sur "Ajouter une question"
                                        pour commencer.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Soumission */}
                        <div className="mt-6">
                            <button
                                type="submit"
                                className="w-full px-4 py-2 bg-africasa-secondary text-white rounded-md hover:bg-africasa-primary"
                                disabled={loading}
                            >
                                {loading ? "Mise à jour..." : "Modifier la formation"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </MainLayout>
    );
};

export default EditFormation;
